import gql from 'graphql-tag';
import { DynamicComponentData, DynamicNumberComponentData, StaticComponentData } from './component';
import { FilterByComponent } from './viewTemplate';

export const AnyOperandValue = gql`
  fragment AnyOperandValue on AnyOperandValue {
    ... on ActionResultNumberOperand {
      actionId
      type
    }
    ... on ComponentOperand {
      componentId
      scalarKey
      type
    }
    ... on PreviousComponentOperand {
      componentId
      scalarKey
      type
    }
    ... on NumberOperand {
      numberValue
      type
    }
    ... on DateOperand {
      dateValue
      type
    }
    ... on DateTokenOperand {
      dateToken
      type
    }
    ... on StringArrayOperand {
      stringValues
      type
    }
    ... on NumberArrayOperand {
      numberValues
      type
    }
    ... on StringOperand {
      stringValue
      type
    }
    ... on BooleanOperand {
      booleanValue
      type
    }
    ... on WorkflowEntityRefComponentOperand {
      componentId
      refComponentId
      refWorkflowTemplateId
      scalarKey
      type
    }
    ... on SubformComponentRefOperand {
      subformComponentId
      refComponentType
      cases {
        componentId
        workflowTemplateId
      }
      scalarKey
      type
    }
    ... on EmptyOperand {
      type
    }
  }
`;

export const AutomationCondition = gql`
  fragment AutomationCondition on Condition {
    id
    comparisonOperator
    leftOperand {
      ...AnyOperandValue
    }
    logicalOperator
    rightOperand {
      ...AnyOperandValue
    }
  }

  ${AnyOperandValue}
`;

export const CalculateValueAction = gql`
  fragment CalculateValueAction on CalculateValueAction {
    id
    type
    name
    conditions {
      ...AutomationCondition
    }
    settings {
      type
      leftOperand {
        ...AnyOperandValue
      }
      operator
      rightOperand {
        ...AnyOperandValue
      }
    }
  }

  ${AnyOperandValue}
  ${AutomationCondition}
`;

export const UpdateRelatedCardQuantityAction = gql`
  fragment UpdateRelatedCardQuantityAction on UpdateRelatedCardQuantityAction {
    id
    type
    name
    conditions {
      ...AutomationCondition
    }
    settings {
      type
      staticData {
        ...StaticComponentData
      }
      dynamicData {
        ...DynamicNumberComponentData
      }
      relatedCardComponentId
      workflowTemplateId
    }
  }

  ${AutomationCondition}
  ${StaticComponentData}
  ${DynamicNumberComponentData}
`;

export const SendNotificationAction = gql`
  fragment SendNotificationAction on SendNotificationAction {
    id
    type
    name
    conditions {
      ...AutomationCondition
    }
    settings {
      type
      message
      recipientEmails
      recipientsFromEmailComponentIds
      replyTo
      subject
      workflowTemplateId
    }
  }
  ${AutomationCondition}
`;

export const SendEmailAction = gql`
  fragment SendEmailAction on SendEmailAction {
    id
    type
    name
    conditions {
      ...AutomationCondition
    }
    settings {
      type
      message
      recipientEmails
      recipientsFromEmailComponentIds
      recipientsFromRelatedCardEmail {
        emailComponentId
        relatedCardComponentId
      }
      replyTo
      replyToCardComponentId
      replyToRelatedCardEmail {
        emailComponentId
        relatedCardComponentId
      }
      subject
      workflowTemplateId
    }
  }

  ${AutomationCondition}
`;

export const SendPushNotificationAction = gql`
  fragment SendPushNotificationAction on SendPushNotificationAction {
    id
    type
    name
    conditions {
      ...AutomationCondition
    }
    settings {
      type
      message
      recipientUserIds
      recipientsFromPersonComponentIds
      title
      workflowTemplateId
    }
  }

  ${AutomationCondition}
`;

export const WorkflowEntityCreateAction = gql`
  fragment WorkflowEntityCreateAction on WorkflowEntityCreateAction {
    id
    type
    name
    conditions {
      ...AutomationCondition
    }
    settings {
      type
      workflowTemplateId
      fields {
        componentId
        staticData {
          ...StaticComponentData
        }
        dynamicData {
          ...DynamicComponentData
        }
      }
    }
  }

  ${StaticComponentData}
  ${DynamicComponentData}
  ${AutomationCondition}
`;

export const WorkflowEntityUpdateAction = gql`
  fragment WorkflowEntityUpdateAction on WorkflowEntityUpdateAction {
    id
    type
    name
    conditions {
      ...AutomationCondition
    }
    settings {
      type
      workflowTemplateId
      currentWorkflowEntityComponent {
        componentId
        type
      }
      fields {
        componentId
        staticData {
          ...StaticComponentData
        }
        dynamicData {
          ...DynamicComponentData
        }
      }
    }
  }

  ${StaticComponentData}
  ${DynamicComponentData}
  ${AutomationCondition}
`;

export const CurrentWorkflowEntityUpdateAction = gql`
  fragment CurrentWorkflowEntityUpdateAction on CurrentWorkflowEntityUpdateAction {
    id
    type
    name
    conditions {
      ...AutomationCondition
    }
    settings {
      type
      workflowTemplateId
      fields {
        componentId
        staticData {
          ...StaticComponentData
        }
        dynamicData {
          ...DynamicComponentData
        }
      }
    }
  }

  ${StaticComponentData}
  ${DynamicComponentData}
  ${AutomationCondition}
`;

export const ExternalFormLinkCreateAction = gql`
  fragment ExternalFormLinkCreateAction on ExternalFormLinkCreateAction {
    id
    type
    name
    conditions {
      ...AutomationCondition
    }
    settings {
      type
      workflowTemplateId
      viewTemplateId
      fields {
        componentId
        staticData {
          ...StaticComponentData
        }
        dynamicData {
          ...DynamicComponentData
        }
      }
    }
  }
  ${StaticComponentData}
  ${DynamicComponentData}
  ${AutomationCondition}
`;

export const ReferencedInQuantitySumAction = gql`
  fragment ReferencedInQuantitySumAction on ReferencedInQuantitySumAction {
    id
    type
    name
    conditions {
      ...AutomationCondition
    }
    settings {
      type
      summationAggregates {
        workflowTemplateId
        componentId
        inverted
        filterByComponents {
          ...FilterByComponent
        }
      }
    }
  }

  ${AutomationCondition}
  ${FilterByComponent}
`;

export const TriggerRelatedCardAutomationAction = gql`
  fragment TriggerRelatedCardAutomationAction on TriggerRelatedCardAutomationAction {
    id
    type
    name
    conditions {
      ...AutomationCondition
    }
    settings {
      type
      automationId
      relatedCardComponentId
      workflowTemplateId
    }
  }

  ${AutomationCondition}
`;

export const SendWebhookAction = gql`
  fragment SendWebhookAction on SendWebhookAction {
    id
    type
    name
    conditions {
      ...AutomationCondition
    }
    parentId
    settings {
      type
      destinationUrl
      secretKey
    }
  }

  ${AutomationCondition}
`;

export const Automation = gql`
  fragment Automation on Automation {
    createdAt
    id
    name
    trigger
    updatedAt
    enabled
    suppressAutomationChain
    workflowTemplateId
    actions {
      ... on CalculateValueAction {
        ...CalculateValueAction
      }
      ... on UpdateRelatedCardQuantityAction {
        ...UpdateRelatedCardQuantityAction
      }
      ... on SendNotificationAction {
        ...SendNotificationAction
      }
      ... on SendEmailAction {
        ...SendEmailAction
      }
      ... on SendPushNotificationAction {
        ...SendPushNotificationAction
      }
      ... on WorkflowEntityCreateAction {
        ...WorkflowEntityCreateAction
      }
      ... on CurrentWorkflowEntityUpdateAction {
        ...CurrentWorkflowEntityUpdateAction
      }
      ... on WorkflowEntityUpdateAction {
        ...WorkflowEntityUpdateAction
      }
      ... on ExternalFormLinkCreateAction {
        ...ExternalFormLinkCreateAction
      }
      ... on ReferencedInQuantitySumAction {
        ...ReferencedInQuantitySumAction
      }
      ... on TriggerRelatedCardAutomationAction {
        ...TriggerRelatedCardAutomationAction
      }
      ... on SendWebhookAction {
        ...SendWebhookAction
      }
    }
    conditions {
      ...AutomationCondition
    }
  }

  ${CalculateValueAction}
  ${UpdateRelatedCardQuantityAction}
  ${ReferencedInQuantitySumAction}
  ${SendEmailAction}
  ${SendPushNotificationAction}
  ${SendNotificationAction}
  ${WorkflowEntityCreateAction}
  ${WorkflowEntityUpdateAction}
  ${CurrentWorkflowEntityUpdateAction}
  ${ExternalFormLinkCreateAction}
  ${TriggerRelatedCardAutomationAction}
  ${SendWebhookAction}
  ${AutomationCondition}
`;
